/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarNavigationItem } from '@app/components/layouts/main/sider/sidebarNavigation';
import { useHttpApi } from '@app/api/core/http.core.api';
import { iconMap, resolveIconDynamic } from '@app/constants/iconMap';
import { MenuNodeDto } from '@app/modules/aclAdmin/api/aclAdmin.types';

interface UseSidebarNavigationResult {
  navigation: SidebarNavigationItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSidebarNavigation = (): UseSidebarNavigationResult => {
  const http = useHttpApi();
  const { t, i18n } = useTranslation();

  const [navigation, setNavigation] = useState<SidebarNavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Evita setState depois de unmount
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  /** Converte DTO recursivo → estrutura usada pelo Sider */
  const dtoToNav = useCallback(
    (nodes: MenuNodeDto[]): SidebarNavigationItem[] =>
      nodes
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map<SidebarNavigationItem>((n) => ({
          key: n.menuId.toString(),
          title: t(n.translationKey),
          url: n.routePath || undefined,
          // 1ª tentativa: curated map; fallback: dinâmico
          icon: n.iconKey
            ? iconMap[n.iconKey] ?? resolveIconDynamic(n.iconKey)
            : undefined,
          children: dtoToNav(n.children ?? []),
        })),
    [t]
  );
  /** Faz a chamada na API (pode ser reutilizada no refetch) */
  const fetchMenus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await http.get('/v1/acl/my/menus');
      const tree: MenuNodeDto[] = res.data?.data ?? [];

      if (mounted.current) setNavigation(dtoToNav(tree));
    } catch (err: any) {
      if (mounted.current) setError(err?.message ?? 'Erro desconhecido');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [http, dtoToNav]);

  /* Carrega inicialmente e sempre que o idioma mudar para refazer as traduções */
  useEffect(() => {
    fetchMenus();
  }, [fetchMenus, i18n.language]);

  return { navigation, loading, error, refetch: fetchMenus };
};
